import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import classNames from "classnames";

import utils from "@utils";
import NonRKPayInvoiceEstimatesService from "@services/NonRKPayInvoiceEstimatesService";

import "./AddToEstimateModal.scss";

const AddToEstimateModal = ({ show, onHide, jobs, user, onCreateNewEstimate, handleAddToEstimate}) => {
	const { lang } = global;
	const [estimates, setEstimates] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [selectedEstimate, setSelectedEstimate] = useState("");
	const [pagination, setPagination] = useState({
        pageNumber: 1,
        hasMore: false
    });
	const [job] = Object.values(jobs);
	const { ClientId, ClientName } = job || {};

	const handleOnSave = async () => {
		try {
			setLoading(true);
			const jobIds = Object.values(jobs)
				.map(({ Id }) => Id)
				.join(",");
			const queryParams = {
				clientId: ClientId,
				estimate: true,
				id: selectedEstimate,
				jobs: jobIds,
			};
			handleAddToEstimate(queryParams);
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error adding to estimate"),
			});
		} finally {
			setLoading(false);
		}
	};

	const loadEstimates = async (pageNumber = 1, append = false) => {
		try {
			if (pageNumber === 1) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			const invoiceTypeId = utils.mapJobInvoicedTypeIdToInvoiceTypeId(job);
			const queryParams = {
				displayCount: 25,
				pageNumber,
				archived: false,
				paid: false,
				clientId: ClientId,
				sortBy: "InvoiceNumber",
				asc: true,
				estimateFinalized: false,
				invoiceType: invoiceTypeId,
			};
			const {
				data: { ItemList, Count, Page },
			} = await NonRKPayInvoiceEstimatesService.getPaginatedEstimates(queryParams);
            if (append) {
                setEstimates(prev => [...prev, ...ItemList]);
            } else {
                setEstimates(ItemList);
            }
            const totalItemsLoaded = append ? estimates.length + ItemList.length : ItemList.length;
            const hasMore = totalItemsLoaded < Count;

			setPagination({
                pageNumber: Page,
                hasMore
            });
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error loading estimates"),
			});
		} finally {
			setLoading(false);
            setLoadingMore(false);
		}
	};
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

        if (isNearBottom && pagination.hasMore && !loadingMore && !loading) {
            loadEstimates(pagination.pageNumber + 1, true);
        }
    }, [pagination.hasMore, pagination.pageNumber, loadingMore, loading, estimates.length]);

	const onCancel = () => {
		setLoading(false);
        setLoadingMore(false);
        setSelectedEstimate("");
        setEstimates([]);
        setPagination({ pageNumber: 1, hasMore: false });
        onHide();
	};

	useEffect(() => {
		if (show && ClientId) {
			loadEstimates();
		}
	}, [show, jobs]);

	return (
		<Modal
			id="add-jobs-to-estimate"
			show={show}
			onHide={onCancel}
		>
			<Modal.Header closeButton>
				<Modal.Title className="text-center">{lang.t("Add Job to estimate")}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div className="margin-bottom-lg">{ClientName}</div>

				<div className="flex flex-justify-content-end">
					<button
						type="button"
						className="btn btn-link"
						onClick={onCreateNewEstimate}
					>
						+ {lang.t("Create New Estimate")}
					</button>
				</div>

				<div
					className="row hidden-xs hidden-sm margin-top-15"
					id="add-jobs-to-estimtate-header"
				>
					<div className="col-md-3">
						<strong>{lang.t("Estimate")} #</strong>
					</div>
					<div className="col-md-3">
						<strong>{lang.t("Created Date")}</strong>
					</div>
					<div className="col-md-7" />
				</div>

				<div className="row">
					<div
						id="add-jobs-to-estimate-body"
						onScroll={handleScroll}
					>
						<For
							each="item"
							of={estimates}
						>
							<div
								key={item.Id}
								className={classNames("v-align-sm", {
									selected: item.Id === selectedEstimate,
								})}
								onClick={() => setSelectedEstimate(item.Id)}
								role="button"
								tabIndex="0"
								onKeyPress={null}
							>
								<div
									className="col-sm-12 col-md-3 truncate"
									title={item.InvoiceNumber}
								>
									{item.InvoiceNumber}
								</div>
								<div
									className="col-sm-12 col-md-3 truncate"
									title={item.StartDateTime}
								>
									{utils.formatDate(
										item.IssueDate,
										"en-US",
										user.TimeZoneId,
										"numeric",
									)}
								</div>
							</div>
						</For>
						<If condition={loading}>
							<div className="text-center">
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</div>
						</If>
						<If condition={loadingMore}>
                            <div className="text-center">
                                <i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
                            </div>
                        </If>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer className="text-center">
				<button
					type="button"
					className="btn btn-primary"
					onClick={handleOnSave}
					disabled={!selectedEstimate}
				>
					{lang.t("Add to Estimate")}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

AddToEstimateModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	jobs: PropTypes.array.isRequired,
	onCreateNewEstimate: PropTypes.func.isRequired,
	handleAddToEstimate: PropTypes.func.isRequired,
	user: PropTypes.object,
};

AddToEstimateModal.defaultProps = {
	user: {},
};

export default AddToEstimateModal;
